import { getMinutes, getHours, addDays, endOfDay, startOfDay } from 'date-fns'
import { Capacitor } from '@capacitor/core'
import { orderBy } from 'lodash'

import Api from 'services/Api'
import { createErrorNotification } from 'store/actions/notifications'
import { store } from 'store/reducers'
import { t } from 'translations'
import { Format, Parse, localizedStartOfWeek, localizedEndOfWeek } from 'services/Dates'
import merinioSvgImage from 'assets/images/merinioLogo'
import { getProjectName, handleMobileSaveFiles, hasAccessToThis } from 'services/Utilities'
import { getPositionName } from 'services/utilFunctions/getPositionName'
import { getUserName } from 'services/utilFunctions/getUserName'

const datesBetween = (startDate, endDate) => {
  let start = new Date(startDate)
  const end = new Date(endDate)
  const dates = []
  while (start <= end) {
    dates.push(start)
    start = addDays(start, 1)
  }
  return dates
}

export default async (userId, range) => {
  let rangeStart = localizedStartOfWeek(range[0])
  let rangeEnd = localizedEndOfWeek(range[1])

  const dateParams = { rangeStart: Format(rangeStart), rangeEnd: Format(rangeEnd), rangeCheckStartOnly: true }

  const filter = {
    ...dateParams,
    userId,
    alsoMarkPunchEvent: hasAccessToThis('punchCardSee'),
    useNewReturn: true,
    markShiftsForLeaveOrVacation: true,
    returnScheduleContactEvents: true
  }

  // Loads events
  let { success: successEvents, result: events } = await Api.get('schedule/schedule', { params: { ...filter, employeeId: userId } })

  // Only load vacation if user has the correct permissions
  let { success: successVacation, result: vacations } = hasAccessToThis('manageTimeOff')
    ? await Api.get('requests/vacation', {
        params: { ...filter, requestStatusId: 'APPROVED', useNewReturn: true }
      })
    : { success: true, result: [] }

  if (!successVacation || !successEvents) return store.dispatch(dispatch => dispatch(createErrorNotification()))

  vacations = orderBy(vacations, ['start'])
  events = orderBy(events, ['start'])

  // Either group the events by day
  let shiftsPerDay = {}

  let weekDays = datesBetween(rangeStart, rangeEnd)
  const weekDaysDateStrings = weekDays.map(date => Format(date, { outFormat: 'server' }))

  // Adds the events to the shiftsPerDay map
  for (const event of events) {
    const dayOfWeek = weekDaysDateStrings.findIndex(day => day.includes(event.start.slice(0, 10)))
    if (!shiftsPerDay[dayOfWeek]) shiftsPerDay[dayOfWeek] = []
    shiftsPerDay[dayOfWeek].push(event)
  }

  // Adds the vacations to the shiftsPerDay map
  if (vacations) {
    for (const vacation of vacations) {
      // Set a boolean for the report generator (to add the color)
      vacation.isVacation = true

      // See for any weekDay if the user is in vacation, if so, print.
      for (const weekDay of weekDays) {
        if (
          !(new Date(vacation.start) <= weekDay && new Date(vacation.end) >= weekDay) &&
          !(new Date(vacation.start) >= startOfDay(weekDay) && new Date(vacation.end) <= endOfDay(weekDay))
        )
          continue

        const dayOfWeek = weekDays.indexOf(weekDay)

        // Add the vacation to the user in the shift map
        if (!shiftsPerDay[dayOfWeek]) shiftsPerDay[dayOfWeek] = []

        //to avoid printing multiples vacations on the same day
        if (!shiftsPerDay[dayOfWeek].find(day => day.isVacation)) shiftsPerDay[dayOfWeek].push(vacation)
      }
    }
  }

  // Group all the events by week
  let shiftsPerWeek = {}
  const nbOfDays = weekDays.length
  const nbOfWeeks = nbOfDays / 7

  for (let i = 0; i < nbOfWeeks; i++) {
    let weekDates = `${Format(weekDays[i * 7], { outFormat: 'dayAndMonth' })} - ${Format(weekDays[i * 7 + 6], { outFormat: 'dayAndMonth' })}`
    if (!shiftsPerWeek[weekDates]) shiftsPerWeek[weekDates] = {}
    for (let j = i * 7; j < i * 7 + 7; j++)
      if (shiftsPerDay[j]) shiftsPerWeek[weekDates][j] = shiftsPerDay[j]
      else shiftsPerWeek[weekDates][j] = []
  }

  var dd = {
    content: [],
    styles: {
      header: { fontSize: 13, bold: true, marginTop: 3, marginLeft: 20 },
      medium: { fontSize: 7 },
      small: { fontSize: 6 },
      verySmall: { fontSize: 5, marginLeft: -3, marginRight: -3 },
      generatedBy: { fontSize: 12, marginBottom: 10, marginRight: 2, marginTop: 1, color: 'grey', bold: true },
      margin: { marginRight: 20 },
      bold: { bold: true, fontSize: 10, marginBottom: 2, marginTop: 5 }
    },
    footer: {
      columns: [
        { text: [t('manager.schedule.reports.GENERATED_BY')], style: 'generatedBy', alignment: 'right' },
        { image: merinioSvgImage, style: 'margin', width: 60 }
      ],
      marginRight: 10
    },
    pageSize: 'A5',
    pageOrientation: 'landscape',
    // [left, top, right, bottom] or [horizontal, vertical]
    pageMargins: [20, 30, 20, 30]
  }

  // Add header
  dd.header = {
    text: t('employee.mySchedule.SCHEDULE_HEADER', {
      start: Format(rangeStart, { outFormat: 'humanDateCompact' }),
      end: Format(rangeEnd, { outFormat: 'humanDateCompact' }),
      employee: getUserName(userId)
    }),
    style: 'header'
  }

  const weekDaysFormatted = weekDays.map(date => Format(date, { outFormat: 'EEE' }))
  var calendarPrint = {
    table: {
      widths: ['*', '*', '*', '*', '*', '*', '*', '*'],
      heights: ['*'],
      body: [
        [
          { text: '', fillColor: '#eeeeee' },
          { text: t(weekDaysFormatted[0]), style: 'medium', alignment: 'right' },
          { text: t(weekDaysFormatted[1]), style: 'medium', alignment: 'right' },
          { text: t(weekDaysFormatted[2]), style: 'medium', alignment: 'right' },
          { text: t(weekDaysFormatted[3]), style: 'medium', alignment: 'right' },
          { text: t(weekDaysFormatted[4]), style: 'medium', alignment: 'right' },
          { text: t(weekDaysFormatted[5]), style: 'medium', alignment: 'right' },
          { text: t(weekDaysFormatted[6]), style: 'medium', alignment: 'right' }
        ]
      ],
      width: 'auto'
    },
    layout: {
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.5 : 0.25),
      vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 0.5 : 0.25),
      hLineColor: (i, node) => (i === 0 || i === node.table.body.length ? 'black' : 'gray'),
      vLineColor: (i, node) => (i === 0 || i === node.table.widths.length ? 'black' : 'gray')
    }
  }

  // Loops over all the weeks and for each renders a row
  for (const week in shiftsPerWeek) {
    let row = [
      {
        stack: [{ text: week, style: 'medium', alignment: 'center' }],
        margin: [1, 16.5, 1, 1]
      }
    ]
    // Push the height of the row
    calendarPrint.table.heights.push(40)

    Object.keys(shiftsPerWeek[week]).forEach(dayOfWeek => {
      // Shifts the days of the week so that mondays are at position 0 and sundays at position 6
      const offsetDayOfWeek = (parseInt(dayOfWeek) + 7) % 7

      // Get the day
      const day = weekDays[dayOfWeek].getDate()

      let dayNb = {
        text: [
          {
            text: `${Format(weekDays[dayOfWeek], { outFormat: 'd' })} ${day === 1 ? Format(weekDays[dayOfWeek], { outFormat: 'MMM' }) : ''}`
          }
        ],
        margin: [0, 0, 0, 4],
        alignment: 'right',
        style: 'verySmall'
      }

      let cell = { stack: [], margin: [1, 1, 1, 1] }

      // Code for an empty cell
      if (!shiftsPerWeek[week][dayOfWeek].length) cell = { stack: [], fillColor: '#eeeeee', margin: [1, 1, 1, 1] } // Code for an empty cell

      cell.stack.push(dayNb)

      for (const event of shiftsPerWeek[week][dayOfWeek]) {
        const start = Parse(event.start)
        const end = Parse(event.end)

        // If the event is a vacation, render it as a vacation
        if (event.isVacation) {
          cell.fillColor = '#c5b0e4' //'#F6F0FD'
          const isAllDay = getMinutes(start) === 0 && getHours(start) === 0 && getMinutes(end) === 59 && getHours(end) === 23
          cell.stack.push({
            text: [
              {
                text: isAllDay
                  ? t('manager.schedule.reports.ALL_DAY')
                  : `${Format(event.start, { outFormat: 'time' })} - ${Format(event.end, { outFormat: 'time' })}`
              },
              { text: ` ${t('manager.schedule.reports.TIME_OFF')}`, bold: true }
            ],
            style: 'verySmall'
          })

          // If the event is a shift, render it as a shift
        } else {
          cell.stack.push({
            text: [
              { text: getProjectName(event.projects?.[0], { withCode: true, canBeNull: true, noValue: '' }) },
              { text: `${Format(event.start, { outFormat: 'time' })} - ${Format(event.end, { outFormat: 'time' })}` },
              { text: ` ${getPositionName(event.positionId)}`, bold: true }
            ],
            style: 'verySmall'
          })
        }
      }

      // Enter the day's shifts cell into a location in the row corresponding to the correct work day
      // Adjust by 1 since first column in the row is taken up by the week dates
      row[offsetDayOfWeek + 1] = cell
    })

    calendarPrint.table.body.push(row)
  }

  dd.content.push(calendarPrint)

  const fileName = `${t('employee.mySchedule.PDF_TITLE', {
    start: Format(rangeStart, { outFormat: 'date' }),
    end: Format(rangeEnd, { outFormat: 'date' }),
    employee: getUserName(userId)
  })}.pdf`

  const pdfMake = (await import('pdfmake/build/pdfmake.min')).default
  const pdfFonts = (await import('pdfmake/build/vfs_fonts')).default
  pdfMake.vfs = pdfFonts.pdfMake.vfs
  // print the PDF
  if (!Capacitor.isNativePlatform()) pdfMake.createPdf(dd).download(fileName)
  else pdfMake.createPdf(dd).getBase64(b64 => handleMobileSaveFiles(b64, fileName, 'application/pdf'))
}
